import { Loader2, MessageSquareQuote, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  CORE_IDENTITY_SECTION,
  LEXICON_SECTION,
  EMPTY_AUTHENTICITY_MARKER_ROW,
  EMPTY_AVOID_TERM_ROW,
  EMPTY_BUILDING_BLOCK_PROTOCOL_ROW,
  EMPTY_HIGH_FREQUENCY_TERM_ROW,
  EMPTY_MISREPRESENTATION_ROW,
  EMPTY_PARAGRAPH_FLOW_ROW,
  EMPTY_PEAK_EXEMPLAR_ROW,
  EMPTY_RED_LINE_ROW,
  EMPTY_RHETORICAL_ROW,
  EMPTY_SENTENCE_ARCHITECTURE_ROW,
  EMPTY_SIGNATURE_PHRASE_ROW,
  EMPTY_SYNTHESIS_ROW,
  EMPTY_VERBAL_MARKER_ROW,
  VOICE_PROFILE_SECTIONS,
  type AuthenticityMarkerRow,
  type AvoidTermRow,
  type BuildingBlockProtocolRow,
  type HighFrequencyTermRow,
  type MisrepresentationRow,
  type ParagraphFlowRow,
  type PeakExemplarRow,
  type RedLineRow,
  type RhetoricalMoveRow,
  type SentenceArchitectureRow,
  type SignaturePhraseRow,
  type SynthesisRow,
  type VerbalMarkerRow,
  type VoiceDraftState,
  type VoiceProfileFieldKey,
} from "./voiceProfilePanelData";

export type { VoiceDraftState } from "./voiceProfilePanelData";

type Props = {
  voiceDraft: VoiceDraftState;
  setVoiceDraft: React.Dispatch<React.SetStateAction<VoiceDraftState>>;
  savingVoice: boolean;
  onSave: () => void;
};

type ColumnDef<Row> = {
  key: keyof Row;
  label: string;
  placeholder?: string;
  rows?: number;
};

function RowEditor<Row extends Record<string, string>>({
  rows,
  columns,
  onChange,
  emptyRow,
}: {
  rows: Row[];
  columns: ColumnDef<Row>[];
  onChange: (next: Row[]) => void;
  emptyRow: Row;
}) {
  const updateCell = (idx: number, key: keyof Row, value: string) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, [key]: value } : r));
    onChange(next);
  };
  const addRow = () => onChange([...rows, { ...emptyRow }]);
  const removeRow = (idx: number) => {
    const next = rows.filter((_, i) => i !== idx);
    onChange(next.length === 0 ? [{ ...emptyRow }] : next);
  };

  return (
    <div className="space-y-3">
      {/* Column headers — hidden on small screens, shown on md+ */}
      <div className="hidden md:grid gap-2 text-xs font-medium text-muted-foreground" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr)) auto` }}>
        {columns.map((c) => (
          <div key={String(c.key)}>{c.label}</div>
        ))}
        <div className="w-8" aria-hidden />
      </div>

      {rows.map((row, idx) => (
        <div
          key={idx}
          className="grid gap-2 md:items-start"
          style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr)) auto` }}
        >
          {columns.map((c) => (
            <div key={String(c.key)} className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground md:hidden">{c.label}</label>
              <Textarea
                rows={c.rows ?? 2}
                placeholder={c.placeholder ?? c.label}
                value={row[c.key] ?? ""}
                onChange={(e) => updateCell(idx, c.key, e.target.value)}
              />
            </div>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 mt-1 text-muted-foreground hover:text-destructive"
            onClick={() => removeRow(idx)}
            aria-label={`Remove row ${idx + 1}`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}

      <Button type="button" variant="outline" size="sm" onClick={addRow}>
        <Plus className="h-4 w-4" />
        Add row
      </Button>
    </div>
  );
}

const SYNTHESIS_COLUMNS: ColumnDef<SynthesisRow>[] = [
  { key: "referenced_concept", label: "Referenced Concept", placeholder: "e.g. Aristotelian virtue ethics" },
  { key: "usage_pattern", label: "Usage Pattern", placeholder: "How you draw on / push back against it" },
  { key: "key_terms", label: "Key Terms", placeholder: "Vocabulary you associate with it" },
];

const RHETORICAL_COLUMNS: ColumnDef<RhetoricalMoveRow>[] = [
  { key: "move", label: "Move", placeholder: "e.g. Reframe the objection" },
  { key: "description", label: "Description", placeholder: "What this move does for the reader" },
  { key: "example_pattern", label: "Example Pattern", placeholder: "Sentence shape or template you use" },
];
const HIGH_FREQUENCY_TERM_COLUMNS: ColumnDef<HighFrequencyTermRow>[] = [
  { key: "category", label: "Category", placeholder: "e.g. Theological concepts" },
  { key: "terms", label: "Terms", placeholder: "Comma-separated terms in this category" },
];

const SIGNATURE_PHRASE_COLUMNS: ColumnDef<SignaturePhraseRow>[] = [
  { key: "phrase", label: "Phrase", placeholder: "e.g. \"the deeper question is…\"" },
  { key: "how_used", label: "How It Is Used", placeholder: "When and why this phrase appears" },
];

const VERBAL_MARKER_COLUMNS: ColumnDef<VerbalMarkerRow>[] = [
  { key: "marker", label: "Verbal Marker", placeholder: "e.g. \"look…\", \"here's the thing…\"" },
  { key: "how_used", label: "How It Is Used", placeholder: "Context in spoken content" },
];

const SENTENCE_ARCHITECTURE_COLUMNS: ColumnDef<SentenceArchitectureRow>[] = [
  { key: "pattern", label: "Pattern", placeholder: "e.g. Short declarative → qualifying clause" },
  { key: "application", label: "Application", placeholder: "When / why you use this pattern" },
];

const PARAGRAPH_FLOW_COLUMNS: ColumnDef<ParagraphFlowRow>[] = [
  { key: "section", label: "Section", placeholder: "e.g. Opening, Body, Close" },
  { key: "application_flow", label: "Application Flow", placeholder: "How the section moves" },
];

const BUILDING_BLOCK_PROTOCOL_COLUMNS: ColumnDef<BuildingBlockProtocolRow>[] = [
  { key: "protocol", label: "Protocol", placeholder: "e.g. Opening hook" },
  { key: "directive", label: "Directive", placeholder: "The rule the AI must follow for this protocol" },
];

const RED_LINE_COLUMNS: ColumnDef<RedLineRow>[] = [
  { key: "value", label: "Red line", placeholder: "e.g. Content endorsing violence" },
];

const AVOID_TERM_COLUMNS: ColumnDef<AvoidTermRow>[] = [
  { key: "value", label: "Term or concept", placeholder: "e.g. \"synergy\", \"leverage\", hype-marketing framings" },
];

const MISREPRESENTATION_COLUMNS: ColumnDef<MisrepresentationRow>[] = [
  { key: "value", label: "Misrepresentation", placeholder: "e.g. People assume X means Y, when really…" },
];

const AUTHENTICITY_MARKER_COLUMNS: ColumnDef<AuthenticityMarkerRow>[] = [
  { key: "value", label: "Authenticity marker", placeholder: "e.g. Opens with a personal anecdote before the abstract point" },
];

const PEAK_EXEMPLAR_COLUMNS: ColumnDef<PeakExemplarRow>[] = [
  { key: "value", label: "Exemplar paragraph", placeholder: "Paste a short paragraph of writing that sounds unmistakably like you…", rows: 4 },
];




export function VoiceProfilePanel({ voiceDraft, setVoiceDraft, savingVoice, onSave }: Props) {
  const setField = (key: VoiceProfileFieldKey, value: string) =>
    setVoiceDraft((current) => ({ ...current, [key]: value }));

  return (
    <div className="p-4 space-y-4">
      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle>Master Voice Document</CardTitle>
          <CardDescription>
            Keep the source-of-truth writing calibration here. The Planning and Writing agents will anchor every
            Brief and Draft to these rules.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Input
              placeholder="Title"
              value={voiceDraft.title}
              onChange={(e) => setVoiceDraft((current) => ({ ...current, title: e.target.value }))}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle>{CORE_IDENTITY_SECTION.title}</CardTitle>
          {CORE_IDENTITY_SECTION.description ? (
            <CardDescription>{CORE_IDENTITY_SECTION.description}</CardDescription>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-2">
          {CORE_IDENTITY_SECTION.fields.map((f) => (
            <Textarea
              key={f.key}
              rows={f.rows ?? 4}
              placeholder={f.placeholder ?? f.label}
              value={voiceDraft[f.key]}
              onChange={(e) => setField(f.key, e.target.value)}
            />
          ))}
        </CardContent>
      </Card>


      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle>{LEXICON_SECTION.title}</CardTitle>
          {LEXICON_SECTION.description ? (
            <CardDescription>{LEXICON_SECTION.description}</CardDescription>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-6">
          {LEXICON_SECTION.fields.map((f) => (
            <div key={f.key} className="space-y-1">
              <h4 className="text-sm font-medium">{f.label}</h4>
              <Textarea
                rows={f.rows ?? 4}
                placeholder={f.placeholder ?? f.label}
                value={voiceDraft[f.key]}
                onChange={(e) => setField(f.key, e.target.value)}
              />
            </div>
          ))}
          <div className="space-y-2">
            <h4 className="text-sm font-medium">2.2 Building Block Protocols</h4>
            <RowEditor
              rows={voiceDraft.building_block_protocols}
              columns={BUILDING_BLOCK_PROTOCOL_COLUMNS}
              emptyRow={EMPTY_BUILDING_BLOCK_PROTOCOL_ROW}
              onChange={(next) => setVoiceDraft((c) => ({ ...c, building_block_protocols: next }))}
            />
          </div>
        </CardContent>
      </Card>


      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle>{voiceDraft.dual_voice_modes_enabled ? "3. Voice Modes" : "3. Voice"}</CardTitle>
          <CardDescription>
            {voiceDraft.dual_voice_modes_enabled
              ? "Two named modes the agents blend per-Avatar. Default labels are Editorial and Teaching."
              : "Your single working voice. Turn on a second mode below if you want to blend two named voices per audience avatar."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4 rounded-md border border-border bg-muted/30 p-3">
            <div className="space-y-1">
              <p className="text-sm font-medium">Use Two Voice Modes</p>
              <p className="text-xs text-muted-foreground">
                Most writers only need one voice. Turn this on to blend two named modes per audience avatar.
              </p>
            </div>
            <Switch
              checked={voiceDraft.dual_voice_modes_enabled}
              onCheckedChange={(checked) =>
                setVoiceDraft((current) => ({ ...current, dual_voice_modes_enabled: checked }))
              }
            />
          </div>

          <div className={voiceDraft.dual_voice_modes_enabled ? "grid gap-4 md:grid-cols-2" : "space-y-2"}>
            <div className="space-y-2">
              <Input
                placeholder={voiceDraft.dual_voice_modes_enabled ? "Mode A label (e.g. Editorial)" : "Primary Voice"}
                value={voiceDraft.voice_mode_a_label}
                onChange={(e) => setVoiceDraft((current) => ({ ...current, voice_mode_a_label: e.target.value }))}
              />
              <Textarea
                rows={4}
                placeholder={
                  voiceDraft.dual_voice_modes_enabled
                    ? "When this mode is dominant, the voice does…"
                    : "Notes describing how this voice sounds, what it does, what it avoids…"
                }
                value={voiceDraft.voice_mode_a_notes}
                onChange={(e) => setField("voice_mode_a_notes", e.target.value)}
              />
            </div>
            {voiceDraft.dual_voice_modes_enabled ? (
              <div className="space-y-2">
                <Input
                  placeholder="Mode B label (e.g. Teaching)"
                  value={voiceDraft.voice_mode_b_label}
                  onChange={(e) => setVoiceDraft((current) => ({ ...current, voice_mode_b_label: e.target.value }))}
                />
                <Textarea
                  rows={4}
                  placeholder="When this mode is dominant, the voice does…"
                  value={voiceDraft.voice_mode_b_notes}
                  onChange={(e) => setField("voice_mode_b_notes", e.target.value)}
                />
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {VOICE_PROFILE_SECTIONS.map((section) => {
        const rowKind = section.kind;
        if (rowKind) {
          const rowConfig = (() => {
            switch (rowKind) {
              case "rhetorical-moves":
                return {
                  rows: voiceDraft.rhetorical_moves,
                  columns: RHETORICAL_COLUMNS,
                  emptyRow: EMPTY_RHETORICAL_ROW,
                  onChange: (next: RhetoricalMoveRow[]) =>
                    setVoiceDraft((c) => ({ ...c, rhetorical_moves: next })),
                };
              case "synthesis":
                return {
                  rows: voiceDraft.tradition_synthesis,
                  columns: SYNTHESIS_COLUMNS,
                  emptyRow: EMPTY_SYNTHESIS_ROW,
                  onChange: (next: SynthesisRow[]) =>
                    setVoiceDraft((c) => ({ ...c, tradition_synthesis: next })),
                };
              case "vocabulary-fingerprint":
                return null;
              case "structural-patterns":
                return null;
              case "boundaries":
                return null;
              case "exemplars":
                return null;
            }
          })();
          if (rowKind === "exemplars") {
            return (
              <Card key={section.title} className="shadow-soft">
                <CardHeader>
                  <CardTitle>{section.title}</CardTitle>
                  {section.description ? <CardDescription>{section.description}</CardDescription> : null}
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <div>
                      <h4 className="text-sm font-medium">Authenticity Markers</h4>
                      <p className="text-xs text-muted-foreground">
                        What makes something sound like you? What are the tells that the content is authentically yours?
                      </p>
                    </div>
                    <RowEditor
                      rows={voiceDraft.authenticity_markers}
                      columns={AUTHENTICITY_MARKER_COLUMNS}
                      emptyRow={EMPTY_AUTHENTICITY_MARKER_ROW}
                      onChange={(next) => setVoiceDraft((c) => ({ ...c, authenticity_markers: next }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <div>
                      <h4 className="text-sm font-medium">Peak-Author Exemplars</h4>
                      <p className="text-xs text-muted-foreground">
                        Add specific short paragraph examples of writing that reflect your Authenticity Markers.
                      </p>
                    </div>
                    <RowEditor
                      rows={voiceDraft.peak_author_examples}
                      columns={PEAK_EXEMPLAR_COLUMNS}
                      emptyRow={EMPTY_PEAK_EXEMPLAR_ROW}
                      onChange={(next) => setVoiceDraft((c) => ({ ...c, peak_author_examples: next }))}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          }
          if (rowKind === "boundaries") {
            return (
              <Card key={section.title} className="shadow-soft">
                <CardHeader>
                  <CardTitle>{section.title}</CardTitle>
                  {section.description ? <CardDescription>{section.description}</CardDescription> : null}
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <div>
                      <h4 className="text-sm font-medium">Hard Red Lines (Must Never)</h4>
                      <p className="text-xs text-muted-foreground">
                        Add types of content here that you would never publish under your name, regardless of context.
                      </p>
                    </div>
                    <RowEditor
                      rows={voiceDraft.red_lines}
                      columns={RED_LINE_COLUMNS}
                      emptyRow={EMPTY_RED_LINE_ROW}
                      onChange={(next) => setVoiceDraft((c) => ({ ...c, red_lines: next }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <div>
                      <h4 className="text-sm font-medium">Terms &amp; Concepts to Avoid</h4>
                      <p className="text-xs text-muted-foreground">
                        Add specific words, phrases, or framings you actively reject or never use. What makes you cringe in content when you see it?
                      </p>
                    </div>
                    <RowEditor
                      rows={voiceDraft.avoid_list}
                      columns={AVOID_TERM_COLUMNS}
                      emptyRow={EMPTY_AVOID_TERM_ROW}
                      onChange={(next) => setVoiceDraft((c) => ({ ...c, avoid_list: next }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <div>
                      <h4 className="text-sm font-medium">Common Misrepresentations</h4>
                      <p className="text-xs text-muted-foreground">
                        What do people commonly get wrong about your work or field that frustrates you?
                      </p>
                    </div>
                    <RowEditor
                      rows={voiceDraft.common_misrepresentations}
                      columns={MISREPRESENTATION_COLUMNS}
                      emptyRow={EMPTY_MISREPRESENTATION_ROW}
                      onChange={(next) => setVoiceDraft((c) => ({ ...c, common_misrepresentations: next }))}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          }
          if (rowKind === "structural-patterns") {
            return (
              <Card key={section.title} className="shadow-soft">
                <CardHeader>
                  <CardTitle>{section.title}</CardTitle>
                  {section.description ? <CardDescription>{section.description}</CardDescription> : null}
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">4.1 Sentence Architecture</h4>
                    <RowEditor
                      rows={voiceDraft.sentence_architecture}
                      columns={SENTENCE_ARCHITECTURE_COLUMNS}
                      emptyRow={EMPTY_SENTENCE_ARCHITECTURE_ROW}
                      onChange={(next) => setVoiceDraft((c) => ({ ...c, sentence_architecture: next }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">4.2 Paragraph & Article Flow</h4>
                    <RowEditor
                      rows={voiceDraft.paragraph_flow}
                      columns={PARAGRAPH_FLOW_COLUMNS}
                      emptyRow={EMPTY_PARAGRAPH_FLOW_ROW}
                      onChange={(next) => setVoiceDraft((c) => ({ ...c, paragraph_flow: next }))}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          }
          if (rowKind === "vocabulary-fingerprint") {
            return (
              <Card key={section.title} className="shadow-soft">
                <CardHeader>
                  <CardTitle>{section.title}</CardTitle>
                  {section.description ? <CardDescription>{section.description}</CardDescription> : null}
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <div>
                      <h4 className="text-sm font-medium">5.1 High-frequency Terms</h4>
                      <p className="text-xs text-muted-foreground">
                        These terms appear consistently across both written and spoken content and should be used naturally.
                      </p>
                    </div>
                    <RowEditor
                      rows={voiceDraft.vocabulary_fingerprint}
                      columns={HIGH_FREQUENCY_TERM_COLUMNS}
                      emptyRow={EMPTY_HIGH_FREQUENCY_TERM_ROW}
                      onChange={(next) => setVoiceDraft((c) => ({ ...c, vocabulary_fingerprint: next }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">5.2 Signature Phrases</h4>
                    <RowEditor
                      rows={voiceDraft.signature_phrases}
                      columns={SIGNATURE_PHRASE_COLUMNS}
                      emptyRow={EMPTY_SIGNATURE_PHRASE_ROW}
                      onChange={(next) => setVoiceDraft((c) => ({ ...c, signature_phrases: next }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <div>
                      <h4 className="text-sm font-medium">5.3 Verbal Markers (Spoken Voice)</h4>
                      <p className="text-xs text-muted-foreground">
                        These markers appear in natural speech and can be used sparingly in conversational content.
                      </p>
                    </div>
                    <RowEditor
                      rows={voiceDraft.verbal_markers}
                      columns={VERBAL_MARKER_COLUMNS}
                      emptyRow={EMPTY_VERBAL_MARKER_ROW}
                      onChange={(next) => setVoiceDraft((c) => ({ ...c, verbal_markers: next }))}
                    />
                  </div>
                  {section.fields.map((f) => (
                    <div key={f.key} className="space-y-2">
                      <label className="text-sm font-medium">{f.label}</label>
                      <Textarea
                        rows={f.rows ?? 3}
                        placeholder={f.placeholder}
                        value={voiceDraft[f.key]}
                        onChange={(e) => setField(f.key, e.target.value)}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          }
          return (
            <Card key={section.title} className="shadow-soft">
              <CardHeader>
                <CardTitle>{section.title}</CardTitle>
                {section.description ? <CardDescription>{section.description}</CardDescription> : null}
              </CardHeader>
              <CardContent>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- discriminated by section.kind */}
                <RowEditor {...(rowConfig as any)} />
              </CardContent>
            </Card>
          );
        }
        return (
          <Card key={section.title} className="shadow-soft">
            <CardHeader>
              <CardTitle>{section.title}</CardTitle>
              {section.description ? <CardDescription>{section.description}</CardDescription> : null}
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              {section.fields.map((f) => (
                <div key={f.key} className="space-y-2">
                  <label className="text-sm font-medium">{f.label}</label>
                  <Textarea
                    rows={f.rows ?? 3}
                    placeholder={f.placeholder}
                    value={voiceDraft[f.key]}
                    onChange={(e) => setField(f.key, e.target.value)}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })}


      <div className="flex justify-end">
        <Button onClick={onSave} disabled={savingVoice}>
          {savingVoice ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquareQuote className="h-4 w-4" />}
          Save voice profile
        </Button>
      </div>
    </div>
  );
}
